import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import { TOCNode } from '../../../types/DocumentTypes';
import { useDocumentContext } from '../../../components/DocumentContext';
import { useSessionContext } from '../../../components/SessionContext';
import { findNextRootChapter } from '../utils/chapterUtils';
import { useSearchParams } from 'react-router';

type ReaderState = 'loading' | 'ready' | 'error';

interface ReaderContextValue {
  state: ReaderState;
  nextChapter: TOCNode | null; // Next loadable chapter
  previousChapter: TOCNode | null; // Previous loadable chapter
}

const ReaderContext = createContext<ReaderContextValue>({
  state: 'loading',
  nextChapter: null,
  previousChapter: null,
});

export function ReaderContextProvider({
  children,
}: Readonly<PropsWithChildren>) {
  const { chapters } = useDocumentContext();
  const {
    initReadingSession,
    trackProgress,
    navigateToChapter,
    currentChapter,
  } = useSessionContext();
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<ReaderState>('loading');
  const didInitRef = useRef(false);

  const nextChapter = useMemo(() => {
    return findNextRootChapter(currentChapter, chapters, 'next');
  }, [currentChapter, chapters]);

  const previousChapter = useMemo(() => {
    return findNextRootChapter(currentChapter, chapters, 'previous');
  }, [currentChapter, chapters]);

  // Simple initialization - let ProgressManager handle all the complex stuff
  useEffect(() => {
    if (didInitRef.current || state !== 'loading' || !chapters?.length) {
      return;
    }

    const initializeReader = async () => {
      try {
        setState('loading');
        const result = await initReadingSession(chapters, searchParams);

        // Just navigate to the target - ProgressManager handles all business logic
        await navigateToChapter({
          chapter: result.location.chapter,
          pageNumber: result.location.pageNumber,
        });
        setState('ready');
        // If no modal (decision already true), start tracking now
        if (result.action === 'navigate') {
          // Defer to next tick to ensure content is mounted
          setTimeout(() => {
            trackProgress();
          }, 0);
        }
      } catch (_error) {
        setState('error');
      }
    };

    initializeReader();
    didInitRef.current = true;
  }, [state, chapters, searchParams, navigateToChapter, initReadingSession]); // eslint-disable-line react-hooks/exhaustive-deps

  const contextValue: ReaderContextValue = useMemo(
    () => ({
      state,
      nextChapter,
      previousChapter,
    }),
    [state, nextChapter, previousChapter],
  );

  return (
    <ReaderContext.Provider value={contextValue}>
      {children}
    </ReaderContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useReaderContext() {
  const context = useContext(ReaderContext);
  if (context === undefined) {
    throw new Error(
      'useReaderContext must be used within a ReaderContextProvider',
    );
  }
  return context;
}
